import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { tagService } from '../../../services/tags.service';
import Button from '../../UI/Button/Button';
import Input from '../../UI/Input/Input';
import Alert from '../../UI/Alert/Alert';
import styles from '../TagCreateForm/TagCreateForm.module.css';
import { toast } from 'react-toastify';

function TagEditForm({ isOpen, onClose, onSuccess, tagId, initialData }) {
    const { t } = useTranslation('common');
    const { t: tValidation } = useTranslation('validation');

    const [code, setCode] = useState({ value: initialData?.code || '', error: null });
    const [name, setName] = useState({ value: initialData?.name || '', error: null });
    const [generalError, setGeneralError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (isOpen && initialData) {
            setCode({ value: initialData.code, error: null });
            setName({ value: initialData.name, error: null });
        }
    }, [isOpen, initialData]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setCode({ value: code.value, error: null });
        setName({ value: name.value, error: null });
        setGeneralError(null);

        try {
            const response = await tagService.updateTag(tagId, {
                code: code.value,
                name: name.value
            });

            if (response.id) {
                toast.success(tValidation('tag_edited_successfully'));
                if (onSuccess) {
                    onSuccess();
                }
                onClose();
            }
        } catch (error) {
            const errorEntity = error?.payload?.error;
            if (errorEntity && Array.isArray(errorEntity)) {
                errorEntity.forEach(err => {
                    if (err.path[0] === 'code') {
                        setCode({ value: code.value, error: err });
                    }
                    if (err.path[0] === 'name') {
                        setName({ value: name.value, error: err });
                    }
                });
            } else if (typeof errorEntity === 'string') {
                const message = tValidation(errorEntity.toString());
                setGeneralError(message);
                toast.error(message);
            } else {
                const message = error.message || tValidation('failed_to_update_tag');
                setGeneralError(message);
                toast.error(message);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        if (!isLoading) {
            setGeneralError(null);
            onClose();
        }
    };

    if (!isOpen) return null;

    return createPortal(
        <div className={styles.overlay} onKeyDown={(e) => e.stopPropagation()}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h2 className={styles.title}>{t('tags.edit_modal_title')}</h2>
                    <button
                        className={styles.closeButton}
                        onClick={handleClose}
                        disabled={isLoading}
                        type="button"
                    >
                        ×
                    </button>
                </div>
                <form className={styles.form} onSubmit={handleSubmit}>
                    {generalError && (
                        <Alert type="error">
                            <p>{generalError}</p>
                        </Alert>
                    )}
                    <div className={styles.formGroup}>
                        <label className={styles.label}>{t('tags.code_label')}</label>
                        <Input
                            placeholder={t('tags.code_label')}
                            value={code.value}
                            onChange={(e) => setCode({ value: e.target.value.toUpperCase(), error: null })}
                            error={code.error}
                            required
                        />
                    </div>
                    <div className={styles.formGroup}>
                        <label className={styles.label}>{t('tags.name_label')}</label>
                        <Input
                            placeholder={t('tags.name_label')}
                            value={name.value}
                            onChange={(e) => setName({ value: e.target.value, error: null })}
                            error={name.error}
                            required
                        />
                    </div>
                    <div className={styles.buttons}>
                        <Button
                            variant="secondary"
                            type="button"
                            onClick={handleClose}
                            disabled={isLoading}
                        >
                            {t('tags.cancel')}
                        </Button>
                        <Button
                            variant="primary"
                            type="submit"
                            disabled={isLoading}
                        >
                            {t('tags.save')}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
        , document.body);
}

export default TagEditForm;
